import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderOpen, PenLine, Image as ImageIcon, Inbox, Plus, FileEdit } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/site/motion";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);

  const cards = stats
    ? [
        { label: "Projects", value: stats.projects_total, sub: `${stats.projects_published} published · ${stats.projects_draft} drafts`, icon: FolderOpen, id: "stat-projects" },
        { label: "Blog Posts", value: stats.posts_total, sub: `${stats.posts_published} published`, icon: PenLine, id: "stat-posts" },
        { label: "Media Files", value: stats.media_count, sub: "in library", icon: ImageIcon, id: "stat-media" },
        { label: "Inquiries", value: stats.inquiries_count, sub: "from contact form", icon: Inbox, id: "stat-inquiries" },
      ]
    : [];

  return (
    <div data-testid="admin-overview">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">Overview</h1>
          <p className="mt-1 text-sm text-neutral-500">Your content at a glance</p>
        </div>
        <div className="flex gap-3">
          <Link to="/admin/portfolio/new" className="btn-primary !px-5 !py-2.5 text-xs" data-testid="quick-new-project">
            <Plus className="h-4 w-4" /> New Project
          </Link>
          <Link to="/admin/blog/new" className="btn-ghost !px-5 !py-2.5 text-xs" data-testid="quick-new-post">
            <Plus className="h-4 w-4" /> New Post
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {(stats ? cards : Array(4).fill(null)).map((c, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <div className="glass rounded-2xl p-5" data-testid={c?.id || `stat-loading-${i}`}>
              {c ? (
                <>
                  <c.icon className="h-5 w-5 text-neutral-500" />
                  <p className="font-display mt-4 text-3xl font-bold">{c.value}</p>
                  <p className="mt-1 text-xs text-neutral-500">{c.label} — {c.sub}</p>
                </>
              ) : (
                <div className="h-20 animate-pulse rounded bg-white/[0.04]" />
              )}
            </div>
          </Reveal>
        ))}
      </div>

      {analytics && (
        <div className="mt-10" data-testid="overview-analytics-summary">
          <h2 className="font-display mb-4 text-lg font-bold">Website analytics</h2>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[
              ["Total views", analytics.total_views],
              ["Views today", analytics.views_today],
              ["Last 7 days", analytics.views_7d],
              ["Unique visitors", analytics.unique_visitors],
            ].map(([label, value]) => (
              <div key={label} className="glass rounded-2xl p-5">
                <p className="font-display text-2xl font-bold">{value}</p>
                <p className="mt-1 text-xs text-neutral-500">{label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10">
        <h2 className="font-display mb-4 text-lg font-bold">Recent inquiries</h2>
        <div className="glass overflow-hidden rounded-2xl" data-testid="recent-inquiries">
          {!stats || stats.recent_inquiries.length === 0 ? (
            <p className="p-6 text-sm text-neutral-500">No inquiries yet — they appear here when visitors use the contact form.</p>
          ) : (
            stats.recent_inquiries.map((q) => (
              <div key={q.id} className="flex items-start gap-4 border-b border-white/[0.06] p-5 last:border-0">
                <FileEdit className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white">
                    {q.name} <span className="ml-2 text-xs font-normal text-neutral-500">{q.email}</span>
                  </p>
                  <p className="mt-1 line-clamp-2 text-sm text-neutral-400">{q.message}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
